// ./src/components/features/DisplayZoneCard/DisplayZoneCard.tsx
import React, { useState, useMemo } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Select,
  Collapse,
  ActionIcon,
  Badge,
  useMantineTheme,
  Grid,
  TextInput,
} from "@mantine/core";
import { PlayList } from "@/utils/api";
import { AppIcon } from "@/components/generic";
import { PlayListSelector } from "../PlayLists";

import {
  ScheduleFiltersForm,
  ScheduleFiltersFormValue,
} from "../ScheduleFiltersForm";

export interface DisplayZoneCardValue {
  displayZoneId: string;
  displayZoneType: "content_blocks" | "schedule" | null;
  displayZoneTitle?: string | null | undefined;
  playListId?: string | null;
  /** Schedule zones only: playlist to show when there are no current/upcoming events */
  fallbackPlayListId?: string | null;
  scheduleType?: string | null;
  venueId?: string | null;
  roomId?: string | null;
  floor?: string | null;
  roomTypeId?: string | null;
  roomTag?: string | null;
  eventTag?: string | null;
  roomCategoryId?: string | null;
  eventCategoryId?: string | null;
  showPastHours?: number | null;
  showUpcomingHours?: number | null;
}

export interface ZoneLayoutInfo {
  id: string;
  widthPercent: number;
}

export interface DisplayZoneCardProps {
  /** Zone layout information from the layout config */
  zoneLayout: ZoneLayoutInfo;

  /** Current zone configuration value */
  value: DisplayZoneCardValue | null | undefined;

  /** Callback when zone configuration changes */
  onChange: (value: DisplayZoneCardValue) => void;

  /** Venue ID for context */
  venueId?: string | null;

  /** Validation errors for this zone */
  errors?: {
    displayZoneType?: string;
    playListId?: string;
    scheduleType?: string;
  };

  /** Whether the card is disabled */
  disabled?: boolean;

  /** Component size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
}

const ZONE_TYPE_OPTIONS = [
  { value: "content_blocks", label: "Content" },
  { value: "schedule", label: "Schedule" },
];

export const DisplayZoneCard: React.FC<DisplayZoneCardProps> = ({
  zoneLayout,
  value,
  onChange,
  venueId,
  errors,
  disabled = false,
  size = "md",
}) => {
  const theme = useMantineTheme();
  const [isExpanded, setIsExpanded] = useState(true);

  const currentValue: DisplayZoneCardValue = useMemo(
    () =>
      value || {
        displayZoneId: zoneLayout.id,
        displayZoneType: null,
        displayZoneTitle: null,
        playListId: null,
        fallbackPlayListId: null,
        scheduleType: null,
        venueId: null,
        roomId: null,
        floor: null,
        roomTypeId: null,
        roomTag: null,
        eventTag: null,
        roomCategoryId: null,
        eventCategoryId: null,
        showPastHours: null,
        showUpcomingHours: null,
      },
    [value, zoneLayout.id],
  );

  // Get color scheme based on zone type
  const getZoneColor = () => {
    if (!currentValue.displayZoneType) return "gray";
    return currentValue.displayZoneType === "content_blocks" ? "blue" : "green";
  };

  // Handle zone type change
  const handleZoneTypeChange = (newType: string | null) => {
    if (newType === "content_blocks" || newType === "schedule") {
      // Clear fields from the other type when switching
      if (newType === "content_blocks") {
        onChange({
          displayZoneId: zoneLayout.id,
          displayZoneType: newType,
          displayZoneTitle: null,
          playListId: null,
          fallbackPlayListId: null,
          scheduleType: null,
          venueId: null,
          roomId: null,
          floor: null,
          roomTypeId: null,
          roomTag: null,
          eventTag: null,
          roomCategoryId: null,
          eventCategoryId: null,
        });
      } else {
        onChange({
          displayZoneId: zoneLayout.id,
          displayZoneType: newType,
          displayZoneTitle: null,
          playListId: null,
          fallbackPlayListId: null,
          scheduleType: null,
          venueId: null,
          roomId: null,
          floor: null,
          roomTypeId: null,
          roomTag: null,
          eventTag: null,
          showPastHours: null,
          showUpcomingHours: null,
          roomCategoryId: null,
          eventCategoryId: null,
        });
      }
    }
  };

  const handleZoneTitleChange = (value: string | null) => {
    onChange({
      ...currentValue,
      displayZoneTitle: value,
    });
  };

  // Handle playlist change
  const handlePlayListChange = (
    playListId: string | string[] | null,
    _playList?: PlayList | PlayList[],
  ) => {
    onChange({
      ...currentValue,
      playListId: Array.isArray(playListId) ? playListId[0] : playListId,
    });
  };

  // Handle fallback playlist change (schedule zones — shown when no events)
  const handleFallbackPlayListChange = (
    fallbackPlayListId: string | string[] | null,
    _playList?: PlayList | PlayList[],
  ) => {
    onChange({
      ...currentValue,
      fallbackPlayListId: Array.isArray(fallbackPlayListId)
        ? fallbackPlayListId[0]
        : fallbackPlayListId,
    });
  };

  // Handle schedule filters change
  const handleScheduleFiltersChange = (filters: ScheduleFiltersFormValue) => {
    onChange({
      ...currentValue,
      scheduleType: filters.scheduleType,
      venueId: filters.venueId,
      roomId: filters.roomId,
      floor: filters.floor,
      roomTypeId: filters.roomTypeId,
      roomTag: filters.roomTag,
      eventTag: filters.eventTag,
      showPastHours: filters.showPastHours,
      showUpcomingHours: filters.showUpcomingHours,
      roomCategoryId: filters.roomCategoryId,
      eventCategoryId: filters.eventCategoryId,
    });
  };

  const zoneColor = getZoneColor();

  return (
    <Paper
      withBorder
      p="md"
      style={{
        borderLeft: `4px solid ${theme.colors[zoneColor][5]}`,
        backgroundColor: theme.colors[zoneColor][0],
      }}
    >
      <Stack gap="sm">
        {/* Zone Header */}
        <Group justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <AppIcon iconName="Layout" size={18} />
            <Group>
              <Text size="sm" fw={600}>
                {zoneLayout.id}
              </Text>
              <Text size="xs" c="dimmed">
                Width: {zoneLayout.widthPercent}%
              </Text>
            </Group>
          </Group>

          <Group gap="xs" wrap="nowrap">
            {currentValue.displayZoneType && (
              <Badge color={zoneColor} variant="light" size="sm">
                {currentValue.displayZoneType === "content_blocks"
                  ? "Content"
                  : "Schedule"}
              </Badge>
            )}
            <ActionIcon
              variant={isExpanded ? "filled" : "subtle"}
              color={zoneColor}
              onClick={() => setIsExpanded(!isExpanded)}
              size="sm"
              disabled={disabled}
            >
              <AppIcon
                iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
                size={16}
              />
            </ActionIcon>
          </Group>
        </Group>

        {/* Zone Configuration */}
        <Collapse in={isExpanded}>
          <Stack gap="md">
            {/* Zone Type Selector */}
            <Select
              label="Zone Type"
              placeholder="Select zone type"
              data={ZONE_TYPE_OPTIONS}
              value={currentValue.displayZoneType}
              onChange={handleZoneTypeChange}
              disabled={disabled}
              size={size}
              required
              withAsterisk
              clearable={false}
              error={errors?.displayZoneType}
            />

            <Grid>
              <Grid.Col span={12}>
                <TextInput
                  label="Zone Title"
                  placeholder="Enter display title for the zone"
                  value={currentValue.displayZoneTitle || ""}
                  onChange={(e) => handleZoneTitleChange(e.target.value)}
                  disabled={disabled}
                  size={size}
                />
              </Grid.Col>
            </Grid>

            {/* Content Configuration */}
            {currentValue.displayZoneType === "content_blocks" && (
              <PlayListSelector
                label="Playlist"
                value={currentValue.playListId || null}
                onChange={handlePlayListChange}
                venueId={venueId}
                placeholder="Select a playlist"
                disabled={disabled}
                size={size}
                required
                withAsterisk
                clearable={false}
                error={errors?.playListId}
              />
            )}

            {/* Schedule Configuration */}
            {currentValue.displayZoneType === "schedule" && (
              <>
                <ScheduleFiltersForm
                  value={{
                    scheduleType: currentValue.scheduleType,
                    venueId: currentValue.venueId,
                    roomId: currentValue.roomId,
                    floor: currentValue.floor,
                    roomTypeId: currentValue.roomTypeId,
                    roomTag: currentValue.roomTag,
                    eventTag: currentValue.eventTag,
                    roomCategoryId: currentValue.roomCategoryId,
                    eventCategoryId: currentValue.eventCategoryId,
                    showPastHours: currentValue.showPastHours,
                    showUpcomingHours: currentValue.showUpcomingHours,
                  }}
                  onChange={handleScheduleFiltersChange}
                  venueId={venueId}
                  disabled={disabled}
                  size={size}
                  error={errors?.scheduleType}
                />

                <PlayListSelector
                  label="Playlist when no events"
                  description="Optional. Shown in this zone when there are no current or upcoming events (empty day or all events passed)."
                  value={currentValue.fallbackPlayListId || null}
                  onChange={handleFallbackPlayListChange}
                  venueId={venueId}
                  placeholder="Select a playlist (optional)"
                  disabled={disabled}
                  size={size}
                />
              </>
            )}
          </Stack>
        </Collapse>
      </Stack>
    </Paper>
  );
};
